import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ReportType } from '@src/types';
import { ReportDashboard } from '@features/admin/reports/ReportDashboard.tsx';
import {
  EvalAccountByDatePaPayoutNewJody,
  EvalAnalysisByCreationDate,
  PaProfitableTraders,
  PayoutsTotalsReport,
  PayoutsPaAnalysisByDate,
  PaTradersWithExcessiveDailyProfit,
  ExpressPAAccountRefunds,
} from '@pages/admin/reports/reportTypesPage';
import { PaDailyPlHitting30PercentOfTarget } from '@pages/admin/reports/reportTypesPage/PaDailyPlHitting30PercentOfTarget.tsx';
import { ActiveUsersWithNoAccounts } from '@pages/admin/reports/reportTypesPage/ActiveUsersWithNoAccounts.tsx';
import { BlacklistedUsers } from '@pages/admin/reports/reportTypesPage/BlacklistedUsers.tsx';
import { ChurnRateByDate } from '@pages/admin/reports/reportTypesPage/ChurnRateByDate.tsx';
import { UsersWithExtraSessions } from '@pages/admin/reports/reportTypesPage/UsersWithExtraSessions.tsx';
import { UsersWithTopOfBook } from '@pages/admin/reports/reportTypesPage/UsersWithTopOfBook.tsx';
import { ActiveUsersExpiringBeforeAccountExpiry } from '@pages/admin/reports/reportTypesPage/ActiveUsersExpiringBeforeAccountExpiry.tsx';
import { UsersWithEurex } from '@pages/admin/reports/reportTypesPage/UsersWithEurex.tsx';
import { PaNewAccountByDate } from '@pages/admin/reports/reportTypesPage/PaNewAccountByDate.tsx';
import { ProfitableTraderV2 } from '@pages/admin/reports/reportTypesPage/ProfitableTraderV2.tsx';
import { PaReset } from '@pages/admin/reports/reportTypesPage/PaReset.tsx';
import { StaffFreebie } from '@pages/admin/reports/reportTypesPage/StaffFreebie.tsx';

import { ActiveUsersByPeriod } from './reportTypesPage/ActiveUsersByPeriod';
import { ActiveUsersByProductCategories } from './reportTypesPage/ActiveUsersByProductCategories';
import { ActiveUsersByProducts } from './reportTypesPage/ActiveUsersByProducts';
import { AmountOfRebills } from './reportTypesPage/AmountOfRebills';
import { AverageAmountByProductsBreakdown } from './reportTypesPage/AverageAmountByProductsBreakdown';
import { AverageLifetimeValueCohortAnalysis } from './reportTypesPage/AverageLifetimeValueCohortAnalysis';
import { AveragePaymentsByUserGroup } from './reportTypesPage/AveragePaymentsByUserGroup';
import { CancelFeedback } from './reportTypesPage/CancelFeedback';
import { ChurnRate } from './reportTypesPage/ChurnRate';
import { ConversionRate } from './reportTypesPage/ConversionRate';
import { CountOfCancellations } from './reportTypesPage/CountOfCancellations';
import { CountOfPendingInvoices } from './reportTypesPage/CountOfPendingInvoices';
import { CountOfSignupByCountry } from './reportTypesPage/CountOfSignupByCountry';
import { CountOfProductPurchase } from './reportTypesPage/CountOfProductPurchase';
import { CountOfProductPurchaseByPaymentSystem } from './reportTypesPage/CountOfProductPurChaseByPaymentSystem';
import { CountOfUserSignupsIncludePending } from './reportTypesPage/CountOfUserSignupsIncludePending';
import { CreditsSpentByProductsBreakdown } from './reportTypesPage/CreditsSpentByProductsBreakdown';
import { DownloadByFilesBreakdown } from './reportTypesPage/DownloadByFilesBreakdown';
import { IncomeReport } from './reportTypesPage/IncomeReport';
import { InvoicesBySignupForm } from './reportTypesPage/InvoicesBySignupForm';
import { NumberOfPaymentsByBillingPlan } from './reportTypesPage/NumberOfPaymentsByBillingPlan';
import { NumberOfRebills } from './reportTypesPage/NumberOfRebills';
import { PaymentDistributionByCustomerCountry } from './reportTypesPage/PaymentDistributionByCustomerCountry';
import { PaymentsByCustomerCity } from './reportTypesPage/PaymentsByCustomerCity';
import { PaymentsByCustomerCountry } from './reportTypesPage/PaymentsByCustomerCountry';
import { PaymentsByNewVsExistingUsers } from './reportTypesPage/PaymentsByNewVsExistingUsers';
import { PaymentsByUsedCoupon } from './reportTypesPage/PaymentsByUsedCoupon';
import { PaymentsByUserGroup } from './reportTypesPage/PaymentsByUserGroup';
import { PaymentsByPaymentSystemBreakdown } from './reportTypesPage/PaymentsByPaymentSystemBreakdown';
import { PaymentsByProductCategoriesBreakdown } from './reportTypesPage/PaymentsByProductCategoriesBreakdown';
import { PaymentsByProductsBreakdown } from './reportTypesPage/PaymentsByProductsBreakdown';
import { PaymentsBySignupFormBreakdown } from './reportTypesPage/PaymentsBySignupFormBreakdown';
import { PaymentsMinusRefundsByCustomerCountry } from './reportTypesPage/PaymentsMinusRefundsByCustomerCountry';
import { PaymentsMinusRefundsByCustomerState } from './reportTypesPage/PaymentsMinusRefundsByCustomerState';
import { PaymentsVsRefunds } from './reportTypesPage/PaymentsVsRefunds';
import { ProductConversion } from './reportTypesPage/ProductConversion';
import { RefundsDistributionByCustomerCountry } from './reportTypesPage/RefundsDistributionByCustomerCountry';
import { RefundsByUsedCoupon } from './reportTypesPage/RefundsByUsedCoupon';
import { RefundsByPaymentSystemBreakdown } from './reportTypesPage/RefundsByPaymentSystemBreakdown';
import { RefundsByProductCategoriesBreakdown } from './reportTypesPage/RefundsByProductCategoriesBreakdown';
import { RefundsByProductBreakdown } from './reportTypesPage/RefundsByProductBreakdown';
import { RetentionRate } from './reportTypesPage/RetentionRate';
import { RollingConversion } from './reportTypesPage/RollingConversion';
import { TaxReport } from './reportTypesPage/TaxReport';
import { TaxByCustomerCountry } from './reportTypesPage/TaxByCustomerCountry';
import { RefundsRate } from './reportTypesPage/RefundsRate';
import { RefundsByCustomerCountry } from './reportTypesPage/RefundsByCustomerCountry';
import { TaxByCustomerState } from './reportTypesPage/TaxByCustomerState';
import { UserDemoGraphics } from './reportTypesPage/UserDemoGraphics';
import { UserDistributionByCustomField } from './reportTypesPage/UserDistributionByCustomField';
import { UserDistributionByCustomFieldByDate } from './reportTypesPage/UserDistributionByCustomFieldByDate';
import { UserLifetime } from './reportTypesPage/UserLifetime';
import { UserByUserGroups } from './reportTypesPage/UserByUserGroups';
import { EvalAccountByMonth } from './reportTypesPage/EvalAccountByMonth';
import { PADailyPL } from './reportTypesPage/PADailyPL';
import { BundlesPAAccounts } from './reportTypesPage/BundlesPAAccounts';
import { PAPlnAnalysis } from './reportTypesPage/PAPlnAnalysis';
import { PAActivePaypoutsAddons } from './reportTypesPage/PAActivePayoutsAddons';
import { PANewRenewalResetByMonth } from './reportTypesPage/PANewRenewalResetByMonth';
import { OldAffiliateClicks } from './reportTypesPage/OldAffliateClicks';
import { NewAffiliateClicks } from './reportTypesPage/NewAffiliateClicks';
import { NewAffiliateByUsers } from './reportTypesPage/NewAffiliateByUsers';
import { OldAffiliateByUsers } from './reportTypesPage/OldAffiliateByUsers';
import { OldAffiliateCommissions } from './reportTypesPage/OldAffiliateCommissions';
import { NewAffiliateCommissions } from './reportTypesPage/NewAffiliateCommissions';
import { OldAffiliatePayoutAmountByUsers } from './reportTypesPage/OldAffiliatePayoutAmoundByUsers';
import { NewAffiliatePayoutAmountByUsers } from './reportTypesPage/NewAffiliatePayoutAmountByUsers';
import { OldAffiliateCommissionsByProduct } from './reportTypesPage/OldAffiliateCommissionsByProduct';
import { NewAffiliateCommissionsByProduct } from './reportTypesPage/NewAffiliateCommissionsByProduct';
import { OldAffiliateSaleNumber } from './reportTypesPage/OldAffiliateSaleNumber';
import { NewAffiliateSaleNumber } from './reportTypesPage/NewAffiliateSaleNumber';
import { LeaderBoard } from './reportTypesPage/LeaderBoard';

export const ReportsPage = () => {
  const location = useLocation();

  const route = useMemo(() => {
    const parseData = queryString.parse(location.search);

    if (!parseData) {
      return null;
    }

    return parseData as unknown as { type: ReportType };
  }, [location.search]);

  const renderContent = useCallback(() => {
    switch (route?.type) {
      case ReportType.USERS_NEW_USERS_BY_MONTH:
        return <ReportDashboard />;
      case ReportType.USERS_ACTIVE_USERS_WITH_NO_ACTIVE_ACCOUNTS:
        return <ActiveUsersWithNoAccounts />;
      case ReportType.USERS_ACTIVE_USERS_EXPIRING_BEFORE_ACCOUNT_EXPIRY:
        return <ActiveUsersExpiringBeforeAccountExpiry />;
      case ReportType.USERS_ACTIVE_USERS_WITH_TOP_OF_BOOK:
        return <UsersWithTopOfBook />;
      case ReportType.USERS_ACTIVE_USERS_WITH_EXTRA_SESSION:
        return <UsersWithExtraSessions />;
      case ReportType.USERS_ACTIVE_USERS_WITH_EUREX:
        return <UsersWithEurex />;
      case ReportType.USERS_CHURN_RATE_BY_DATE:
        return <ChurnRateByDate />;
      case ReportType.USERS_BLACKLISTED_USERS:
        return <BlacklistedUsers />;
      case ReportType.EVAL_ACCOUNT_BY_DATE_PA_PAYOUT_NEW_JODY:
        return <EvalAccountByDatePaPayoutNewJody />;
      case ReportType.EVAL_NEW_RENEWAL_BY_MONTH:
        return <EvalAccountByMonth />;
      case ReportType.PA_DAILY_PL:
        return <PADailyPL />;
      case ReportType.EVAL_EVAL_ANALYSIS_BY_CREATION_DATE:
        return <EvalAnalysisByCreationDate />;
      case ReportType.BUNDLES_PA_ACCOUNTS:
        return <BundlesPAAccounts />;
      case ReportType.PA_DAILY_PL_HITTING_30_PERCENT_OF_TARGET:
        return <PaDailyPlHitting30PercentOfTarget />;
      case ReportType.PA_NEW_ACCOUNTS_BY_DATE:
        return <PaNewAccountByDate />;
      case ReportType.PA_ACTIVE_LIFETIME_PA:
        return <PaTradersWithExcessiveDailyProfit />;
      case ReportType.PA_PROFITABLE_TRADERS:
        return <PaProfitableTraders />;
      case ReportType.PA_PNL_ANALYSIS:
        return <PAPlnAnalysis />;
      case ReportType.PA_RESETS:
        return <PaReset />;
      case ReportType.PA_TYPE_ACTIVE_PAYOUTS_ADDONS_V2:
        return <PAActivePaypoutsAddons />;
      case ReportType.PA_NEW_RENEWAL_RESET_BY_MONTH:
        return <PANewRenewalResetByMonth />;
      case ReportType.PA_PROFITABLE_TRADERS_V2:
        return <ProfitableTraderV2 />;
      case ReportType.PAYOUTS_TOTALS_REPORT:
        return <PayoutsTotalsReport />;
      case ReportType.PAYOUTS_PA_ANALYSIS_BY_DATE:
        return <PayoutsPaAnalysisByDate />;
      case ReportType.LEADERBOARD:
        return <LeaderBoard />;
      case ReportType.STAFF_STAFF_FREEBIES_BY_MONTH:
        return <StaffFreebie />;
      case ReportType.ACTIVE_USERS_BY_PERIOD:
        return <ActiveUsersByPeriod />;
      case ReportType.ACTIVE_USERS_BY_PRODUCT_CATEGORIES:
        return <ActiveUsersByProductCategories />;
      case ReportType.ACTIVE_USERS_BY_PRODUCTS:
        return <ActiveUsersByProducts />;
      case ReportType.AMOUNT_OF_REBILLS:
        return <AmountOfRebills />;
      case ReportType.AVERAGE_AMOUNT_BY_PRODUCTS_BREAKDOWN:
        return <AverageAmountByProductsBreakdown />;
      case ReportType.AVERAGE_LIFETIME_VALUE_COHORT_ANALYSIS:
        return <AverageLifetimeValueCohortAnalysis />;
      case ReportType.AVERAGE_PAYMENTS_BY_USER_GROUP:
        return <AveragePaymentsByUserGroup />;
      case ReportType.CANCEL_FEEDBACK:
        return <CancelFeedback />;
      case ReportType.CHURN_RATE:
        return <ChurnRate />;
      case ReportType.CONVERSION_RATE_BY_PAYMENT_SYSTEM_BREAKDOWN:
        return <ConversionRate />;
      case ReportType.COUNT_OF_CANCELLATIONS:
        return <CountOfCancellations />;
      case ReportType.COUNT_OF_PENDING_INVOICES:
        return <CountOfPendingInvoices />;
      case ReportType.COUNT_OF_SIGNUPS_BY_COUNTRY:
        return <CountOfSignupByCountry />;
      case ReportType.COUNT_OF_PRODUCT_PURCHASE:
        return <CountOfProductPurchase />;
      case ReportType.COUNT_OF_PRODUCT_PURCHASE_BY_PAYMENT_SYSTEM_BREAKDOWN:
        return <CountOfProductPurchaseByPaymentSystem />;
      case ReportType.COUNT_OF_USER_SIGNUPS:
        return <CountOfUserSignupsIncludePending />;
      case ReportType.CREDITS_SPENT_BY_PRODUCTS_BREAKDOWN:
        return <CreditsSpentByProductsBreakdown />;
      case ReportType.DOWNLOADS_BY_FILES_BREAKDOWN:
        return <DownloadByFilesBreakdown />;
      case ReportType.INCOME_REPORT:
        return <IncomeReport />;
      case ReportType.INVOICES_BY_SIGNUP_FORM:
        return <InvoicesBySignupForm />;
      case ReportType.NUMBER_OF_PAYMENTS_BY_BILLING_PLAN:
        return <NumberOfPaymentsByBillingPlan />;
      case ReportType.NUMBER_OF_REBILLS:
        return <NumberOfRebills />;
      case ReportType.PAYMENTS_DISTRIBUTION_BY_CUSTOMER_COUNTRY:
        return <PaymentDistributionByCustomerCountry />;
      case ReportType.PAYMENTS_BY_CUSTOMER_CITY:
        return <PaymentsByCustomerCity />;
      case ReportType.PAYMENTS_BY_CUSTOMER_COUNTRY:
        return <PaymentsByCustomerCountry />;
      case ReportType.PAYMENTS_BY_NEW_VS_EXISTING_MEMBERS:
        return <PaymentsByNewVsExistingUsers />;
      case ReportType.PAYMENTS_BY_USED_COUPON:
        return <PaymentsByUsedCoupon />;
      case ReportType.PAYMENTS_BY_USER_GROUP:
        return <PaymentsByUserGroup />;
      case ReportType.PAYMENTS_BY_PAYMENT_SYSTEM_BREAKDOWN:
        return <PaymentsByPaymentSystemBreakdown />;
      case ReportType.PAYMENTS_BY_PRODUCT_CATEGORIES_BREAKDOWN:
        return <PaymentsByProductCategoriesBreakdown />;
      case ReportType.PAYMENTS_BY_PRODUCTS_BREAKDOWN:
        return <PaymentsByProductsBreakdown />;
      case ReportType.PAYMENTS_BY_SIGNUP_FORM_BREAKDOWN:
        return <PaymentsBySignupFormBreakdown />;
      case ReportType.PAYMENTS_MINUS_REFUNDS_BY_CUSTOMER_COUNTRY:
        return <PaymentsMinusRefundsByCustomerCountry />;
      case ReportType.PAYMENTS_MINUS_REFUNDS_BY_CUSTOMER_STATE:
        return <PaymentsMinusRefundsByCustomerState />;
      case ReportType.PAYMENTS_VS_REFUNDS:
        return <PaymentsVsRefunds />;
      case ReportType.PRODUCT_CONVERSION:
        return <ProductConversion />;
      case ReportType.REFUNDS_DISTRIBUTION_BY_CUSTOMER_COUNTRY:
        return <RefundsDistributionByCustomerCountry />;
      case ReportType.REFUNDS_BY_USED_COUPON:
        return <RefundsByUsedCoupon />;
      case ReportType.REFUNDS_BY_PAYMENT_SYSTEM_BREAKDOWN:
        return <RefundsByPaymentSystemBreakdown />;
      case ReportType.REFUNDS_BY_PRODUCT_CATEGORIES_BREAKDOWN:
        return <RefundsByProductCategoriesBreakdown />;
      case ReportType.REFUNDS_BY_PRODUCTS_BREAKDOWN:
        return <RefundsByProductBreakdown />;
      case ReportType.REFUNDS_RATE:
        return <RefundsRate />;
      case ReportType.REFUNDS_BY_CUSTOMER_COUNTRY:
        return <RefundsByCustomerCountry />;
      case ReportType.RETENTION_RATE:
        return <RetentionRate />;
      case ReportType.ROLLING_CONVERSION:
        return <RollingConversion />;
      case ReportType.TAX_REPORT:
        return <TaxReport />;
      case ReportType.TAX_BY_CUSTOMER_COUNTRY:
        return <TaxByCustomerCountry />;
      case ReportType.TAX_BY_CUSTOMER_STATE:
        return <TaxByCustomerState />;
      case ReportType.USER_DEMOGRAPHICS:
        return <UserDemoGraphics />;
      case ReportType.USER_DISTRIBUTION_BY_CUSTOM_FIELD:
        return <UserDistributionByCustomField />;
      case ReportType.USER_DISTRIBUTION_BY_CUSTOM_FIELD_BY_DATE:
        return <UserDistributionByCustomFieldByDate />;
      case ReportType.USER_LIFETIME:
        return <UserLifetime />;
      case ReportType.USERS_BY_USER_GROUPS:
        return <UserByUserGroups />;
      case ReportType.OLD_AFFILIATE_CLICKS:
        return <OldAffiliateClicks />;
      case ReportType.NEW_AFFILIATE_CLICKS:
        return <NewAffiliateClicks />;
      case ReportType.OLD_AFFILIATE_COMMISSIONS_BY_USER:
        return <OldAffiliateByUsers />;
      case ReportType.NEW_AFFILIATE_COMMISSIONS_BY_USER:
        return <NewAffiliateByUsers />;
      case ReportType.OLD_AFFILIATE_COMMISSIONS:
        return <OldAffiliateCommissions />;
      case ReportType.NEW_AFFILIATE_COMMISSIONS:
        return <NewAffiliateCommissions />;
      case ReportType.OLD_AFFILIATE_PAYOUT_AMOUNT:
        return <OldAffiliatePayoutAmountByUsers />;
      case ReportType.NEW_AFFILIATE_PAYOUT_AMOUNT:
        return <NewAffiliatePayoutAmountByUsers />;
      case ReportType.OLD_AFFILIATE_COMMISSIONS_BY_PRODUCTS:
        return <OldAffiliateCommissionsByProduct />;
      case ReportType.NEW_AFFILIATE_COMMISSIONS_BY_PRODUCTS:
        return <NewAffiliateCommissionsByProduct />;
      case ReportType.OLD_AFFILIATE_SALES_NUMBER:
        return <OldAffiliateSaleNumber />;
      case ReportType.NEW_AFFILIATE_SALES_NUMBER:
        return <NewAffiliateSaleNumber />;
      case ReportType.EXPRESS_ACCOUNT_PA_REFUNDS:
        return <ExpressPAAccountRefunds />;
      default:
        return <></>;
    }
  }, [route?.type]);

  if (route?.type) {
    return renderContent();
  }

  return <ReportDashboard />;
};
